// The homepage's "agents have trusted Cloudish" section: the running total of
// API keys (an agent is just a key) week by week over the last 12 weeks,
// from this app's
// own GET /stats.json (a cached copy of the control plane's /api/v1/stats).
// Weeks are rolling, ending today, so the last point is always today's total
// and every point's "new" count spans a full seven days.
// Renders nothing until that loads, and nothing if it fails: a broken count
// is worse than no count.

import { useEffect, useRef, useState } from 'react';

interface StatsResponse {
  current: { apiKeys: number };
  timeSeries: { apiKeys: { date: string; cumulative: number }[] };
}

interface Week {
  start: string; // YYYY-MM-DD, UTC: the week's first day
  date: string; // YYYY-MM-DD, UTC: the week's last day
  total: number;
  added: number;
}

const WEEKS = 12;
const HEIGHT = 240;
const PAD = { top: 18, bottom: 30 };

/** Thousands-separated, everywhere a count is shown: 1,284, not 1284. */
const fmt = (n: number) => n.toLocaleString('en-US');

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

/** The last WEEKS seven-day weeks ending today (UTC, the control plane's day
 *  boundary). A week's total is the running total as of its last day, which
 *  carries forward over days with no signups. */
function lastWeeks(series: StatsResponse['timeSeries']['apiKeys']): Week[] {
  const lastSeen = series.length ? series[series.length - 1].date : '';
  const today = isoDay(new Date());
  const end = new Date(`${lastSeen > today ? lastSeen : today}T00:00:00Z`);

  // The running total as of a day: the last point on or before it.
  const totalOn = (date: string) => {
    let total = 0;
    for (const p of series) if (p.date <= date) total = p.cumulative;
    return total;
  };

  const weeks: Week[] = [];
  for (let i = WEEKS - 1; i >= 0; i--) {
    const last = new Date(end);
    last.setUTCDate(end.getUTCDate() - 7 * i);
    const first = new Date(last);
    first.setUTCDate(last.getUTCDate() - 6);
    const before = new Date(last);
    before.setUTCDate(last.getUTCDate() - 7);
    const total = totalOn(isoDay(last));
    weeks.push({ start: isoDay(first), date: isoDay(last), total, added: total - totalOn(isoDay(before)) });
  }
  return weeks;
}

const shortDate = (date: string) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

const weekRange = (w: Week) => `${shortDate(w.start)} – ${shortDate(w.date)}`;

/** A clean step (1, 2, 5, 10, 20, 50, ...) giving about four gridlines. */
function niceStep(max: number) {
  const raw = Math.max(max, 1) / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  // Whole agents only: never a step under 1, even for a tiny total.
  return Math.max(1, [1, 2, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? 10 * mag);
}

function useStats() {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/stats.json')
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('unavailable'))))
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  return stats;
}

function Chart({ days }: { days: Week[] }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);
  const [hover, setHover] = useState<number | null>(null);

  // Drawn at the container's real pixel width (not a scaled viewBox), so
  // text and line weights stay true on every screen size.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(280, Math.round(entry.contentRect.width))));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const step = niceStep(days[days.length - 1].total);
  const yMax = Math.ceil(Math.max(days[days.length - 1].total, 1) / step) * step;
  const ticks = Array.from({ length: yMax / step + 1 }, (_, i) => i * step);
  // Side padding grows with the labels it holds (the axis's widest tick on
  // the left, the end total on the right), so "10,000" never spills out.
  // Character widths are the tick's 11px mono and the label's 13px semibold.
  const left = 14 + fmt(yMax).length * 6.7;
  const right = 18 + fmt(days[days.length - 1].total).length * 8;
  const plotW = width - left - right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const X = (i: number) => left + (i / (days.length - 1)) * plotW;
  const Y = (v: number) => PAD.top + plotH - (v / yMax) * plotH;
  const baseY = Y(0);

  const line = days.map((d, i) => `${i ? 'L' : 'M'} ${X(i)} ${Y(d.total)}`).join(' ');
  const area = `${line} L ${X(days.length - 1)} ${baseY} L ${X(0)} ${baseY} Z`;
  const last = days.length - 1;
  // Every fourth week is labeled, counted back from today so today always is.
  const labeled = days.map((_, i) => (last - i) % 4 === 0);
  const active = hover;

  return (
    <div className="agents-chart">
      <div className="agents-plot" ref={boxRef}>
        <svg
          width={width}
          height={HEIGHT}
          role="img"
          aria-label={`Total agents per week, last ${WEEKS} weeks: ${fmt(days[last].total)} today`}
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id="agents-stroke" gradientUnits="userSpaceOnUse" x1={left} y1="0" x2={left + plotW} y2="0">
              <stop offset="0" className="agents-stop-a" />
              <stop offset="1" className="agents-stop-b" />
            </linearGradient>
          </defs>

          {ticks.map((t) => (
            <g key={t}>
              <line x1={left} x2={left + plotW} y1={Y(t)} y2={Y(t)} className="agents-grid" />
              <text x={left - 10} y={Y(t) + 4} textAnchor="end" className="agents-tick">
                {fmt(t)}
              </text>
            </g>
          ))}
          {days.map((d, i) =>
            labeled[i] ? (
              <text key={d.date} x={X(i)} y={HEIGHT - 8} textAnchor="middle" className="agents-tick">
                {i === last ? 'Today' : shortDate(d.date)}
              </text>
            ) : null,
          )}

          <path d={area} fill="url(#agents-stroke)" className="agents-area" />
          <path d={line} fill="none" stroke="url(#agents-stroke)" className="agents-line" />

          {active !== null ? (
            <line x1={X(active)} x2={X(active)} y1={PAD.top} y2={baseY} className="agents-crosshair" />
          ) : null}
          <circle cx={X(active ?? last)} cy={Y(days[active ?? last].total)} r="5" className="agents-dot" />
          {active === null ? (
            <text x={X(last) + 10} y={Y(days[last].total) + 4} className="agents-end-label">
              {fmt(days[last].total)}
            </text>
          ) : null}

          {/* Hit targets: one full-height band per week, wider than any mark. */}
          {days.map((d, i) => (
            <rect
              key={d.date}
              x={X(i) - plotW / (days.length - 1) / 2}
              y={PAD.top}
              width={plotW / (days.length - 1)}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onTouchStart={() => setHover(i)}
            />
          ))}
        </svg>

        {active !== null ? (
          <div
            className="agents-tooltip"
            style={{ left: Math.min(Math.max(X(active), 70), width - 70), top: Y(days[active].total) - 12 }}
          >
            <span className="agents-tooltip-date">{weekRange(days[active])}</span>
            <strong>
              {fmt(days[active].total)} agent{days[active].total === 1 ? '' : 's'}
            </strong>
            <span className="agents-tooltip-added">
              {days[active].added ? `+${fmt(days[active].added)} new` : 'no new keys'}
            </span>
          </div>
        ) : null}
      </div>

      <table className="sr-only">
        <caption>Total agents per week, last {WEEKS} weeks</caption>
        <thead>
          <tr>
            <th>Week</th>
            <th>Total agents</th>
            <th>New</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => (
            <tr key={d.date}>
              <td>{weekRange(d)}</td>
              <td>{fmt(d.total)}</td>
              <td>{fmt(d.added)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AgentsTrust() {
  const stats = useStats();
  if (!stats) return null;
  const days = lastWeeks(stats.timeSeries.apiKeys);
  const total = stats.current.apiKeys;
  const recent = days.reduce((sum, d) => sum + d.added, 0);

  return (
    <section className="section" id="adoption" style={{ paddingTop: 0 }}>
      <div className="section-wide">
        <div className="section-header">
          <span className="eyebrow">Adoption</span>
          <h2>
            <span className="agents-count hero-gradient-text">{fmt(total)}</span> agents have trusted Cloudish
          </h2>
          <p className="muted">
            {recent < total
              ? `${fmt(recent)} of them joined in the last ${WEEKS} weeks.`
              : `Every one of them joined in the last ${WEEKS} weeks.`}
          </p>
        </div>
        <Chart days={days} />
      </div>
    </section>
  );
}
